<template>
  <div class="overlay">
    <div class="modal">
      <h2>{{ turnoEditar ? 'Editar Turno' : (esBloqueo ? 'Bloqueo / Descanso' : 'Nuevo Turno') }}</h2>

      <!-- Agendar un paciente o bloquear un horario (descanso/almuerzo). -->
      <div v-if="!turnoEditar && !pacientePredefinido" class="tipo-turno">
        <button type="button" :class="{ activo: !esBloqueo }" @click="esBloqueo = false">Paciente</button>
        <button type="button" :class="{ activo: esBloqueo }" @click="esBloqueo = true">Bloqueo / Descanso</button>
      </div>

      <template v-if="!esBloqueo">
      <div class="campo">
        <label for="nt-nombre">Nombre</label>
        <div class="autocomplete-container">
          <!-- autocomplete="off": son datos del paciente, no deben quedar en el autofill. -->
          <input
            v-model="nombre"
            id="nt-nombre" name="nt-nombre" autocomplete="off"
            placeholder="Nombre del paciente"
            :disabled="!!turnoEditar || !!pacientePredefinido"
            @input="onEditarNombreManual"
            @focus="mostrarSugerencias = true"
            @blur="ocultarSugerenciasConDelay"
          />
          <div v-if="mostrarSugerencias && pacientesFiltrados.length > 0" class="sugerencias-dropdown">
            <div
              v-for="p in pacientesFiltrados"
              :key="p.id"
              class="sugerencia-item"
              @mousedown.prevent="seleccionarPaciente(p)"
            >
              {{ p.nombre }}
            </div>
          </div>
        </div>
      </div>

      <div class="campo">
        <label for="nt-apellido">Apellido</label>
        <div class="autocomplete-container">
          <input
            v-model="apellido"
            id="nt-apellido" name="nt-apellido" autocomplete="off"
            placeholder="Apellido del paciente"
            :disabled="!!turnoEditar || !!pacientePredefinido"
            @input="onEditarApellidoManual"
            @focus="mostrarSugerenciasApellido = true"
            @blur="ocultarSugerenciasApellidoConDelay"
          />
          <div v-if="mostrarSugerenciasApellido && pacientesFiltradosApellido.length > 0" class="sugerencias-dropdown">
            <div
              v-for="p in pacientesFiltradosApellido"
              :key="p.id"
              class="sugerencia-item"
              @mousedown.prevent="seleccionarPaciente(p)"
            >
              {{ p.nombre }}
            </div>
          </div>
        </div>
      </div>

      <p v-if="turnoEditar" class="hint-cambiar-paciente">
        🔒 Para cambiar de paciente, eliminá esta cita y creá una nueva.
      </p>
      <p v-else-if="pacientePredefinido" class="hint-cambiar-paciente">
        🔒 Estás agendando para este paciente desde su ficha.
      </p>
      </template>

      <div class="campo">
        <label for="nt-motivo">{{ esBloqueo ? 'Título' : 'Motivo' }}</label>
        <div class="autocomplete-container">
          <input
            v-model="motivo"
            id="nt-motivo" name="nt-motivo" autocomplete="off"
            :placeholder="esBloqueo ? 'Ej: Descanso, Almuerzo' : 'Motivo (p. ej. Consulta)'"
            @input="mostrarSugerenciasMotivo = true"
            @focus="mostrarSugerenciasMotivo = true"
            @blur="ocultarSugerenciasMotivoConDelay"
          />
          <div v-if="mostrarSugerenciasMotivo && motivosFiltrados.length > 0" class="sugerencias-dropdown">
            <div 
              v-for="t in motivosFiltrados" 
              :key="t.id" 
              class="sugerencia-item"
              @mousedown.prevent="seleccionarMotivo(t)"
            >
              {{ t.nombre }}
            </div>
          </div>
        </div>
      </div>

      <div class="campo">
        <label for="nt-fecha">Fecha</label>
        <input
          type="date"
          id="nt-fecha"
          name="nt-fecha"
          v-model="fechaLocal"
          class="input-fecha-moderna"
        />
      </div>

      <!-- Bloqueo de día completo: para un feriado o unas vacaciones no tiene sentido pedir
           hora de inicio y duración (el tope del menú son 2 horas). -->
      <template v-if="esBloqueo && !turnoEditar">
        <label class="check-bloqueo">
          <input type="checkbox" id="nt-todo-el-dia" name="nt-todo-el-dia" v-model="bloqueoTodoElDia" />
          <span>Todo el día</span>
        </label>
        <div v-if="bloqueoTodoElDia" class="campo">
          <label for="nt-repetir-hasta">Repetir hasta (opcional)</label>
          <input
            type="date"
            id="nt-repetir-hasta"
            name="nt-repetir-hasta"
            v-model="bloqueoRepetirHasta"
            :min="fechaLocal"
            class="input-fecha-moderna"
          />
          <span class="hint-bloqueo">{{ resumenBloqueo }}</span>
        </div>
      </template>

      <!-- HORAS CORREGIDAS -->
      <div class="campo" v-if="!bloqueoDiaCompleto">
        <label for="nt-inicio">Inicio</label>
        <select v-model="inicioLocal" id="nt-inicio" name="nt-inicio">
          <option v-for="h in horarios" :key="h" :value="h">{{ h }}</option>
        </select>
      </div>
      <div class="campo" v-if="!bloqueoDiaCompleto">
        <label for="nt-duracion">Duración</label>
        <select v-model="duracion" id="nt-duracion" name="nt-duracion">
          <option :value="15">15 minutos</option>
          <option :value="30">30 minutos</option>
          <option :value="45">45 minutos</option>
          <option :value="60">1 hora</option>
          <option :value="90">1 hora 30 min</option>
          <option :value="120">2 horas</option>
        </select>
      </div>

      <div class="acciones">
        <button @click="guardar" :disabled="guardando">
          {{ guardando ? "Guardando…" : "Guardar" }}
        </button>
        <button @click="$emit('cerrar')" :disabled="guardando">Cancelar</button>
      </div>
    </div>
  </div>

  <!-- Los avisos de éxito/error salen por el diálogo global (ConfirmDialog en App.vue).
       Los dos modales de abajo son a medida (ícono/título propios) y quedan acá. -->

  <!-- Modal advertencia fecha pasada -->
  <div v-if="modalFechaPasada.mostrar" class="modal-overlay-alerta">
    <div class="modal-alerta">
      <div class="modal-alerta-header" style="background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);">
        <span class="modal-icono">📅</span>
        <h3>Fecha en el pasado</h3>
      </div>
      <div class="modal-alerta-body">
        <p>Estás agendando un turno en una fecha que ya pasó. ¿Deseas continuar de todas formas?</p>
      </div>
      <div class="modal-alerta-footer">
        <button class="btn-modal btn-cancelar-fecha" @click="cancelarFechaPasada">Cancelar</button>
        <button class="btn-modal btn-continuar-fecha" @click="confirmarFechaPasada">Continuar</button>
      </div>
    </div>
  </div>

  <!-- Modal: el paciente tipeado está deshabilitado -->
  <div v-if="modalReactivar.mostrar" class="modal-overlay-alerta">
    <div class="modal-alerta">
      <div class="modal-alerta-header" style="background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);">
        <span class="modal-icono">🔔</span>
        <h3>Paciente deshabilitado</h3>
      </div>
      <div class="modal-alerta-body">
        <p>«{{ modalReactivar.paciente?.nombre }}» está deshabilitado. ¿Querés reactivarlo y agendar el turno?</p>
      </div>
      <div class="modal-alerta-footer">
        <button class="btn-modal btn-cancelar-fecha" @click="cancelarReactivar">Cancelar</button>
        <button class="btn-modal btn-continuar-fecha" @click="confirmarReactivar">Reactivar y agendar</button>
      </div>
    </div>
  </div>

  <!-- Modal de pago pendiente -->
  <ModalPagoPendiente
    :mostrar="mostrarModalPago"
    @cerrar="cerrarModalPago"
  />
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from "vue";
import { marcarEdicion } from "@/composables/edicionPendiente";
import ModalPagoPendiente from "@/components/ModalPagoPendiente.vue";
import { usePagoVerificacion } from "@/composables/usePagoVerificacion";
import { usePacientesStore } from "@/stores/pacientes";
import { useScrollLock } from "@/composables/useScrollLock";
import { separarNombreApellido } from "@/utils/nombrePaciente";
import { useDialog } from "@/composables/useDialog";
import { useHorarioAgenda } from "@/composables/useHorarioAgenda";
import { diaEnPlural } from "@/utils/horarios";

const props = defineProps({
  fecha: String,
  inicio: String,
  turnos: { type: Array, default: () => [] },
  pacientePredefinido: String,
  pacienteIdPredefinido: { type: [String, Number], default: null },
  turnoEditar: { type: Object, default: null }
});

const emit = defineEmits(["cerrar", "guardar"]);

const { mostrarModalPago, verificarPermisoPago, cerrarModalPago } = usePagoVerificacion();
const pacientesStore = usePacientesStore();
// exito renombrado para no chocar con el booleano `exito` de alTerminar.
const { alerta, exito: exitoDialog, confirmar } = useDialog();
const { slotsPara, esDiaActivo, apertura, cierre } = useHorarioAgenda();

const nombre = ref("");
const apellido = ref("");
const pacienteIdSeleccionado = ref(null);
const mostrarSugerencias = ref(false);
const mostrarSugerenciasApellido = ref(false);
const mostrarSugerenciasMotivo = ref(false);
const motivo = ref("");
const esBloqueo = ref(false); // true = bloqueo/descanso (sin paciente)
// Bloqueo de día completo (feriado, congreso, vacaciones). `bloqueoRepetirHasta` vacío = un
// solo día; con fecha, se crea un bloqueo por cada día del rango.
const bloqueoTodoElDia = ref(false);
const bloqueoRepetirHasta = ref("");
// Evita el doble guardado: un doble toque en Guardar (fácil en celular con la red lenta)
// disparaba `ejecutarGuardado` dos veces y creaba el turno DUPLICADO en la agenda.
const guardando = ref(false);
const fechaLocal = ref("");
const inicioLocal = ref("");
const finLocal = ref("");
const duracion = ref(30);

// Modal advertencia fecha pasada
const modalFechaPasada = ref({ mostrar: false });
const modalReactivar = ref({ mostrar: false, paciente: null });

function esFechaPasada(fecha) {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return new Date(fecha + 'T00:00:00') < hoy;
}

// Cargar pacientes al montar. El bloqueo de scroll del fondo lo maneja useScrollLock
// (contador compartido) para que abrir este modal desde otro no desbloquee de más.
onMounted(() => {
  pacientesStore.cargarDesdeLocalStorage();
  // Mientras el formulario de cita esté abierto, no dejar que la auto-actualización
  // recargue la app y borre lo que se está cargando.
  marcarEdicion("turno", true);
});
onUnmounted(() => marcarEdicion("turno", false));

// El modal solo existe mientras está abierto (v-if), así que basta con bloquear siempre.
useScrollLock(() => true);

// Resetear pacienteId SOLO cuando el usuario edita el campo a mano (@input real).
// Con un watch de [nombre,apellido] no servía: se dispara también con los sets
// programáticos (seleccionarPaciente, precarga) y, por ser asíncrono, borraba el id
// JUSTO DESPUÉS de elegir del buscador. `@input` no se dispara en sets programáticos.
function onEditarNombreManual() {
  mostrarSugerencias.value = true;
  pacienteIdSeleccionado.value = null;
}

function onEditarApellidoManual() {
  mostrarSugerenciasApellido.value = true;
  pacienteIdSeleccionado.value = null;
}

// Al pasar a modo Bloqueo, precargar "Descanso" como título (si el campo está vacío) para
// que la doctora no tenga que escribirlo; si quiere otra cosa, lo borra y escribe. Al volver
// a modo Paciente, limpiar solo si quedó el valor por defecto (no pisa un motivo tipeado).
watch(esBloqueo, (val) => {
  if (val) {
    if (!motivo.value.trim()) motivo.value = "Descanso";
  } else if (motivo.value === "Descanso") {
    motivo.value = "";
  }
});

// Filtrar pacientes según nombre
const pacientesFiltrados = computed(() => {
  if (!nombre.value || nombre.value.length < 1) return [];

  const busqueda = nombre.value.toLowerCase();
  return pacientesStore.pacientes
    .filter(p => p.activo !== false &&p.nombre.toLowerCase().includes(busqueda))
    .slice(0, 5);
});

// Filtrar pacientes según apellido
const pacientesFiltradosApellido = computed(() => {
  if (!apellido.value || apellido.value.length < 1) return [];

  const busqueda = apellido.value.toLowerCase();
  return pacientesStore.pacientes
    .filter(p => p.activo !== false &&p.nombre.toLowerCase().includes(busqueda))
    .slice(0, 5);
});

// Filtrar tratamientos según lo que escribe el usuario en motivo
const motivosFiltrados = computed(() => {
  if (!motivo.value || motivo.value.length < 1) return [];
  
  const busqueda = motivo.value.toLowerCase();
  return pacientesStore.tratamientos
    .filter(t => t.nombre.toLowerCase().includes(busqueda))
    .slice(0, 5); // Máximo 5 sugerencias
});

function seleccionarPaciente(p) {
  // Usa el apellido real del paciente si está guardado; si no (pacientes viejos),
  // el helper cae al split por último espacio.
  const sep = separarNombreApellido(p.nombre, p.apellido);
  nombre.value = sep.nombre;
  apellido.value = sep.apellido;
  pacienteIdSeleccionado.value = p.id;
  mostrarSugerencias.value = false;
  mostrarSugerenciasApellido.value = false;
}

function seleccionarMotivo(t) {
  motivo.value = t.nombre;
  mostrarSugerenciasMotivo.value = false;
}

function ocultarSugerenciasConDelay() {
  setTimeout(() => {
    mostrarSugerencias.value = false;
  }, 200);
}

function ocultarSugerenciasApellidoConDelay() {
  setTimeout(() => {
    mostrarSugerenciasApellido.value = false;
  }, 200);
}

function ocultarSugerenciasMotivoConDelay() {
  // Delay para permitir el click en la sugerencia
  setTimeout(() => {
    mostrarSugerenciasMotivo.value = false;
  }, 200);
}

// Horas elegibles: el horario de trabajo configurado en Ajustes (por defecto 09:00 a 22:30).
// Se agrega SIEMPRE la hora ya seleccionada: al editar un turno viejo que quedó fuera del rango
// (o que vino importado de Google Calendar a una hora rara) el select se quedaba en blanco y al
// guardar se le cambiaba la hora al paciente sin querer.
const horarios = computed(() => slotsPara([{ inicio: inicioLocal.value }]));

const bloqueoDiaCompleto = computed(
  () => esBloqueo.value && !props.turnoEditar && bloqueoTodoElDia.value,
);

function sumarDias(fechaStr, dias) {
  const [a, m, d] = String(fechaStr).split('-').map(Number);
  const f = new Date(a, m - 1, d + dias);
  return `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}-${String(f.getDate()).padStart(2, '0')}`;
}

// Días que va a tapar el bloqueo. Se saltean los que el usuario ya tiene apagados en Ajustes:
// crear un bloqueo ahí serían documentos que no cambian nada.
const MAX_DIAS_BLOQUEO = 90;
const fechasBloqueo = computed(() => {
  if (!bloqueoDiaCompleto.value || !fechaLocal.value) return [];
  const hasta =
    bloqueoRepetirHasta.value && bloqueoRepetirHasta.value >= fechaLocal.value
      ? bloqueoRepetirHasta.value
      : fechaLocal.value;
  const fechas = [];
  let dia = fechaLocal.value;
  // El tope evita que un rango tipeado mal (un año entero) dispare cientos de escrituras.
  while (dia <= hasta && fechas.length < MAX_DIAS_BLOQUEO) {
    if (esDiaActivo(dia)) fechas.push(dia);
    dia = sumarDias(dia, 1);
  }
  return fechas;
});

// Turnos con paciente que caen en esos días. No se tocan: quedan agendados y visibles, esto es
// solo para avisar antes de bloquear.
const turnosEnBloqueo = computed(() =>
  props.turnos.filter(
    (t) =>
      t.estado !== 'eliminada' &&
      t.tipo !== 'bloqueo' &&
      fechasBloqueo.value.includes(t.fecha),
  ),
);

const resumenBloqueo = computed(() => {
  const total = fechasBloqueo.value.length;
  if (total === 0) return 'No hay días para bloquear (los del rango ya están apagados en Ajustes).';
  const dias = total === 1 ? '1 día' : `${total} días`;
  const conTurnos = turnosEnBloqueo.value.length;
  if (conTurnos === 0) return `Se bloquea ${dias}.`;
  return `Se bloquea ${dias}. Ojo: hay ${conTurnos === 1 ? '1 turno agendado' : `${conTurnos} turnos agendados`} ahí.`;
});

// Cargar fecha inicial desde props
watch(
  () => props.fecha,
  (val) => {
    if (val) fechaLocal.value = val;
  },
  { immediate: true },
);

// ⬅️ si viene una hora desde la agenda, se precarga
watch(
  () => props.inicio,
  (val) => {
    if (val) inicioLocal.value = val;
  },
  { immediate: true },
);

// Si viene un paciente predefinido, cargarlo
watch(
  () => props.pacientePredefinido,
  (val) => {
    if (val) {
      // Si vino el id (se agenda desde la ficha), buscar por id — así homónimos no se
      // confunden; si no, caer a buscar por nombre para leer el apellido guardado.
      const p = props.pacienteIdPredefinido
        ? pacientesStore.pacientes.find((x) => String(x.id) === String(props.pacienteIdPredefinido))
        : pacientesStore.pacientes.find((x) => x.nombre && x.nombre.toLowerCase() === val.toLowerCase());
      const sep = separarNombreApellido(val, p?.apellido);
      nombre.value = sep.nombre;
      apellido.value = sep.apellido;
      // Ligar el turno a la ficha exacta desde el arranque (el watch [nombre,apellido]
      // no lo pisa porque el nombre está bloqueado cuando hay pacientePredefinido).
      if (props.pacienteIdPredefinido) {
        pacienteIdSeleccionado.value = props.pacienteIdPredefinido;
      }
    }
  },
  { immediate: true }
);

// Calcular fin automáticamente según inicio + duración
watch(
  [inicioLocal, duracion],
  ([inicio, dur]) => {
    if (inicio && typeof inicio === 'string') {
      const [h, m] = inicio.split(":").map(Number);
      const totalMin = h * 60 + m + dur;
      const hFin = Math.floor(totalMin / 60);
      const mFin = totalMin % 60;
      finLocal.value = `${String(hFin).padStart(2, "0")}:${String(mFin).padStart(2, "0")}`;
    }
  },
  { immediate: true },
);

// Precargar campos cuando se edita un turno existente
watch(
  () => props.turnoEditar,
  (turno) => {
    if (!turno) return;
    // Precargar con el apellido real del paciente (por pacienteId); si no se
    // encuentra o no tiene apellido, el helper cae al split del nombre del turno.
    const p = turno.pacienteId
      ? pacientesStore.pacientes.find((x) => x.id === turno.pacienteId)
      : null;
    const sep = separarNombreApellido(turno.paciente || '', p?.apellido);
    nombre.value = sep.nombre;
    apellido.value = sep.apellido;
    pacienteIdSeleccionado.value = turno.pacienteId || null;
    motivo.value = turno.motivo || '';
    fechaLocal.value = turno.fecha || '';
    inicioLocal.value = turno.inicio || '';

    if (turno.inicio && turno.fin) {
      const [hI, mI] = turno.inicio.split(':').map(Number);
      const [hF, mF] = turno.fin.split(':').map(Number);
      duracion.value = (hF * 60 + mF) - (hI * 60 + mI);
    }
  },
  { immediate: true },
);

function guardar() {
  if (!verificarPermisoPago()) return;

  // --- Bloqueo / descanso: no requiere paciente, solo ocupa el horario ---
  if (esBloqueo.value && !props.turnoEditar) {
    if (!motivo.value.trim()) {
      alerta("Ingresá un título para el bloqueo (ej: Descanso).");
      return;
    }
    if (!fechaLocal.value) { alerta("Por favor, seleccione una fecha."); return; }

    // --- Bloqueo de día completo (feriado, vacaciones) ---
    if (bloqueoDiaCompleto.value) {
      if (fechasBloqueo.value.length === 0) {
        alerta("Esos días ya están apagados en Ajustes › Horario de trabajo, no hace falta bloquearlos.");
        return;
      }
      // A diferencia de un bloqueo por horas, acá NO se frena por solapamiento: se decidió que
      // marcar el feriado sea posible aunque ya haya pacientes, y que esos turnos queden donde
      // están hasta que se los mueva o cancele a mano. Solo se avisa.
      const conTurnos = turnosEnBloqueo.value.length;
      if (conTurnos > 0) {
        confirmar(
          `Esos días tenés ${conTurnos === 1 ? '1 turno agendado' : `${conTurnos} turnos agendados`}. ` +
          'Se van a quedar en la agenda hasta que los muevas o los canceles. ¿Bloquear igual?',
          () => (esFechaPasada(fechaLocal.value) ? (modalFechaPasada.value.mostrar = true) : ejecutarGuardado()),
          { textoBoton: 'Sí, bloquear' },
        );
        return;
      }
      if (esFechaPasada(fechaLocal.value)) {
        modalFechaPasada.value.mostrar = true;
        return;
      }
      ejecutarGuardado();
      return;
    }

    if (!inicioLocal.value) { alerta("Por favor, seleccione una hora de inicio."); return; }
    if (!finLocal.value || inicioLocal.value >= finLocal.value) {
      alerta("El horario del bloqueo no es válido.");
      return;
    }
    const solapa = props.turnos.find((t) => {
      if (t.estado === 'eliminada') return false;
      return t.fecha === fechaLocal.value && t.inicio && t.fin &&
             inicioLocal.value < t.fin && finLocal.value > t.inicio;
    });
    if (solapa) {
      alerta("Ese horario ya está ocupado en la agenda.");
      return;
    }
    if (esFechaPasada(fechaLocal.value)) {
      modalFechaPasada.value.mostrar = true;
      return;
    }
    ejecutarGuardado();
    return;
  }

  if (!nombre.value.trim()) {
    alerta("Por favor, ingrese el nombre del paciente.");
    return;
  }
  if (!apellido.value.trim()) {
    alerta("Por favor, ingrese el apellido del paciente.");
    return;
  }
  if (!fechaLocal.value) {
    alerta("Por favor, seleccione una fecha.");
    return;
  }
  // Día que el usuario marcó como "no atiendo". Se deja pasar si el turno YA estaba en esa
  // fecha: si alguien bloquea los domingos después de haber agendado uno, tiene que poder
  // seguir editándolo (cambiarle la hora, el motivo) en vez de quedar con un turno intocable.
  if (!esDiaActivo(fechaLocal.value) && fechaLocal.value !== props.turnoEditar?.fecha) {
    alerta(`No atendés ${diaEnPlural(fechaLocal.value)}. Si querés agendar igual, activá ese día en Ajustes › Horario de trabajo.`);
    return;
  }
  if (!inicioLocal.value) {
    alerta("Por favor, seleccione una hora de inicio.");
    return;
  }
  if (!finLocal.value) {
    alerta("Por favor, seleccione una hora de fin.");
    return;
  }
  if (inicioLocal.value >= finLocal.value) {
    alerta("La hora de fin debe ser posterior a la hora de inicio.");
    return;
  }
  if (!motivo.value.trim()) {
    alerta("Por favor, ingrese el motivo de la cita.");
    return;
  }

  // Validar superposición de horarios (excluyendo eliminados y el propio turno al editar)
  const solapamiento = props.turnos.find((t) => {
    if (t.estado === 'eliminada') return false;
    if (props.turnoEditar && String(t.id) === String(props.turnoEditar.id)) return false;
    return t.fecha === fechaLocal.value && t.inicio && t.fin &&
           inicioLocal.value < t.fin && finLocal.value > t.inicio;
  });

  if (solapamiento) {
    alerta("Ya existe un turno agendado en ese horario. Por favor verifique la agenda.");
    return;
  }

  // Si el nombre tipeado coincide con un paciente deshabilitado, pedir confirmación
  // para reactivarlo (evita agendar sobre una ficha dada de baja sin darse cuenta).
  // Solo al crear: al editar, el nombre está bloqueado.
  if (!props.turnoEditar) {
    const nombreCompleto = `${nombre.value.trim()} ${apellido.value.trim()}`;
    const deshabilitado = pacientesStore.pacientes.find(
      (p) => p.activo === false && p.nombre.toLowerCase() === nombreCompleto.toLowerCase()
    );
    if (deshabilitado) {
      modalReactivar.value = { mostrar: true, paciente: deshabilitado };
      return;
    }
  }

  // Si la fecha es anterior a hoy, pedir confirmación antes de guardar
  if (esFechaPasada(fechaLocal.value)) {
    modalFechaPasada.value.mostrar = true;
    return;
  }

  ejecutarGuardado();
}

function ejecutarGuardado() {
  // Punto de no retorno: a partir de acá se emite el turno al padre, que lo escribe en la nube.
  // Si ya estamos guardando, no volver a emitir (si no, se crea dos veces).
  if (guardando.value) return;
  guardando.value = true;

  // El padre (AgendaView) escribe en la nube y nos avisa el resultado REAL por acá.
  // Antes se mostraba "guardado correctamente" ni bien se emitía, sin esperar nada: si la nube
  // fallaba (p. ej. no se podía crear el paciente), la doctora veía el cartel de éxito sobre el
  // modal que todavía decía "Guardando…", y el turno no existía.
  const alTerminar = (msgExito) => (exito, msgError) => {
    guardando.value = false;
    // Al aceptar el aviso de éxito, cerrar el formulario (lo hacía cerrarModalAlerta).
    if (exito) exitoDialog(msgExito, () => emit("cerrar"));
    else alerta(msgError || "No se pudo guardar el turno. Intentá de nuevo.");
  };

  // Bloqueo / descanso: turno sin paciente ni ficha, solo ocupa el horario.
  if (esBloqueo.value && !props.turnoEditar) {
    // Día completo: un bloqueo por día, de la apertura al cierre del horario configurado.
    // Se emite un ARRAY para que la agenda los guarde de una y avise una sola vez.
    if (bloqueoDiaCompleto.value) {
      const bloqueos = fechasBloqueo.value.map((fecha, i) => ({
        id: Date.now() + i, // el id se usa como id de documento: no puede repetirse
        tipo: 'bloqueo',
        diaCompleto: true,
        fecha,
        inicio: apertura.value,
        fin: cierre.value,
        paciente: motivo.value.trim(),
        motivo: 'Bloqueo',
        estado: 'bloqueo',
      }));
      const cuantos = bloqueos.length;
      emit("guardar", bloqueos, alTerminar(
        cuantos === 1 ? "Día bloqueado en la agenda." : `${cuantos} días bloqueados en la agenda.`,
      ));
      return;
    }

    emit("guardar", {
      id: Date.now(),
      tipo: 'bloqueo',
      fecha: fechaLocal.value,
      inicio: inicioLocal.value || props.inicio,
      fin: finLocal.value,
      paciente: motivo.value.trim(), // se muestra como título del bloqueo
      motivo: 'Bloqueo',
      estado: 'bloqueo',
    }, alTerminar("Bloqueo agregado a la agenda."));
    return;
  }

  const nombreCompleto = `${nombre.value.trim()} ${apellido.value.trim()}`;

  if (props.turnoEditar) {
    emit("guardar", {
      ...props.turnoEditar,
      fecha: fechaLocal.value,
      inicio: inicioLocal.value,
      fin: finLocal.value,
      paciente: nombreCompleto,
      pacienteId: pacienteIdSeleccionado.value || props.turnoEditar.pacienteId,
      motivo: motivo.value,
    }, alTerminar("Turno actualizado correctamente."));
  } else {
    emit("guardar", {
      id: Date.now(),
      pacienteId: pacienteIdSeleccionado.value || Date.now(),
      fecha: fechaLocal.value,
      inicio: inicioLocal.value || props.inicio,
      fin: finLocal.value,
      paciente: nombreCompleto,
      motivo: motivo.value,
      estado: "pendiente",
    }, alTerminar("Turno guardado correctamente."));
  }
}

function confirmarFechaPasada() {
  modalFechaPasada.value.mostrar = false;
  ejecutarGuardado();
}

function cancelarFechaPasada() {
  modalFechaPasada.value.mostrar = false;
}

function confirmarReactivar() {
  const p = modalReactivar.value.paciente;
  if (p) {
    pacientesStore.toggleEstadoPaciente(p.id); // estaba deshabilitado → lo reactiva
    pacienteIdSeleccionado.value = p.id;        // el turno queda ligado a esa ficha
  }
  modalReactivar.value = { mostrar: false, paciente: null };
  // Seguir el flujo normal: chequear fecha pasada y guardar.
  if (esFechaPasada(fechaLocal.value)) {
    modalFechaPasada.value.mostrar = true;
    return;
  }
  ejecutarGuardado();
}

function cancelarReactivar() {
  modalReactivar.value = { mostrar: false, paciente: null };
}
</script>

<style scoped>
/* Fondo semitransparente detrás del modal */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

/* Caja del modal */
.modal {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  padding: 20px;
  width: 90%;
  max-width: 400px;
  font-family: system-ui, sans-serif;

  display: flex;
  flex-direction: column;
  align-items: stretch; /* ⬅️ inputs y botones alineados */

  /* Nunca más alto que la pantalla, y si no entra se desliza dentro del modal.
     Sin esto, en iPhone (teclado abierto) el botón Guardar quedaba fuera de la vista.
     dvh tiene en cuenta la barra dinámica de Safari; vh es el respaldo para navegadores viejos. */
  max-height: 90vh;
  max-height: 90dvh;
  overflow-y: auto;
}

.modal h2 {
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: #678AB2;
  text-align: center;
}

select {
  width: 100%;
  padding: 10px;
  margin: 6px 0;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  font-family: system-ui, sans-serif;
}

select:focus {
  outline: none;
  border-color: #678AB2;
  box-shadow: 0 0 4px rgba(103, 138, 178, 0.4);
}

/* Inputs */
.modal input {
  width: 100%;
  padding: 10px;
  margin: 6px 0;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  font-family: system-ui, sans-serif;
}

.modal input:focus {
  outline: none;
  border-color: #678AB2;
  box-shadow: 0 0 4px rgba(103, 138, 178, 0.4);
}

/* Nombre/apellido bloqueados al editar una cita (para no mezclar pacientes) */
.modal input:disabled {
  background: #f1f5f9;
  color: #475569;
  cursor: not-allowed;
}

.hint-cambiar-paciente {
  margin: -2px 0 8px;
  font-size: 12px;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 7px 10px;
}

/* Fecha moderna */
.input-fecha-moderna {
  cursor: pointer;
  font-weight: 500;
  color: #333;
  background: linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%);
  border: 1.5px solid #ddd !important;
  transition: all 0.3s ease;
  position: relative;
}

.input-fecha-moderna:hover {
  border-color: #678AB2 !important;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(103, 138, 178, 0.1);
}

.input-fecha-moderna:focus {
  border-color: #678AB2 !important;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(103, 138, 178, 0.1);
}

/* Hacer que todo el input abra el calendario */
.input-fecha-moderna::-webkit-calendar-picker-indicator {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  cursor: pointer;
  opacity: 0;
}

/* Botones */
.acciones {
  display: flex;
  justify-content: space-between; /* ⬅️ botones en fila */
  margin-top: 16px;
}

.acciones button {
  flex: 1; /* ⬅️ cada botón ocupa mismo ancho */
  margin: 0 4px;
  padding: 10px 0;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.acciones button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.acciones button:first-of-type {
  background: #678AB2;
  color: #fff;
}

.acciones button:first-of-type:hover {
  background: #4d6f96;
}

.acciones button:last-of-type {
  background: #eee;
  color: #333;
}

.acciones button:last-of-type:hover {
  background: #ddd;
}


/* Autocomplete */
.autocomplete-container {
  position: relative;
  width: 100%;
}

.sugerencias-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 8px 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.sugerencia-item {
  padding: 10px;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 14px;
  color: #333;
}

.sugerencia-item:hover {
  background: #f0f0f0;
}

.sugerencia-item:last-child {
  border-radius: 0 0 8px 8px;
}

/* Estilos adicionales para campos */
.campo {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
}

/* Bloqueo de día completo */
.check-bloqueo {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
}

.check-bloqueo input {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  cursor: pointer;
}

.hint-bloqueo {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

/* Selector Paciente / Bloqueo */
.tipo-turno {
  display: flex;
  gap: 6px;
  margin-bottom: 14px;
}
.tipo-turno button {
  flex: 1;
  padding: 9px 8px;
  border: 1px solid #d1d5db;
  background: #f8f9fa;
  color: #6b7280;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.tipo-turno button.activo {
  background: #678AB2;
  border-color: #678AB2;
  color: #fff;
}

.campo label {
  font-size: 13px;
  color: #444;
}

/* Modal Alerta Personalizado */
.modal-overlay-alerta {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-alerta {
  background: white;
  width: 90%;
  max-width: 400px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.2s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-alerta-header {
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.modal-alerta-header.error {
  background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
}

.modal-alerta-header.success {
  background: linear-gradient(135deg, #27ae60 0%, #229954 100%);
}

.modal-icono {
  font-size: 24px;
}

.modal-alerta-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.modal-alerta-body {
  padding: 24px;
  color: #333;
  line-height: 1.6;
}

.modal-alerta-body p {
  margin: 0;
  font-size: 15px;
}

.modal-alerta-footer {
  padding: 16px 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  background: #f8f9fa;
  border-top: 1px solid #e5e7eb;
}

.btn-modal {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-aceptar {
  background: #678AB2;
  color: white;
}

.btn-aceptar:hover {
  background: #4d6f96;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(103, 138, 178, 0.3);
}

.btn-cancelar-fecha {
  background: #e5e7eb;
  color: #333;
}

.btn-cancelar-fecha:hover {
  background: #d1d5db;
}

.btn-continuar-fecha {
  background: #f39c12;
  color: white;
}

.btn-continuar-fecha:hover {
  background: #e67e22;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(243, 156, 18, 0.3);
}
</style>
